import {
  AbstractControl,
  FormArray,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export function hasDuplicates(array: any[]): boolean {
  return new Set(array).size !== array.length;
}

export function hasOverlaps(timeSlots: any[]): boolean {
  for (let i = 0; i < timeSlots.length; i++) {
    for (let j = i + 1; j < timeSlots.length; j++) {
      const a = timeSlots[i];
      const b = timeSlots[j];

      if (
        a.start != '' &&
        b.start != '' &&
        a.start !== null &&
        b.start !== null
      ) {
        if (b.start >= a.start && b.start <= a.end) {
          timeSlots[i].control.setErrors({ overlaps: true });
          timeSlots[j].control.setErrors({ overlaps: true });
          return true;
        } else if (a.start >= b.start && a.start <= b.end) {
          timeSlots[i].control.setErrors({ overlaps: true });
          timeSlots[j].control.setErrors({ overlaps: true });
          return true;
        } else {
          timeSlots[i].control.setErrors();
          timeSlots[j].control.setErrors();
        }
      } else {
        timeSlots[i].control.setErrors();
        timeSlots[j].control.setErrors();
      }
    }
  }
  return false;
}

export function timeSlotsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control && control instanceof FormArray) {
      const timeSlots = control.controls.map((control) => ({
        control,
        start: control.get('startTime')!.value,
        end: control.get('endTime')!.value,
      }));

      if (hasDuplicates(timeSlots)) {
        return { duplicates: true };
      }

      if (hasOverlaps(timeSlots)) {
        return { overlaps: true };
      } else {
        return null;
      }
    }

    return null;
  };
}

export function endNotBeforeStart(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control && control instanceof FormArray) {
      const timeSlots = control.controls.map((control) => ({
        control,
        start: control.get('startTime')!.value,
        end: control.get('endTime')!.value,
      }));

      for (let i = 0; i < timeSlots.length; i++) {
        const a = timeSlots[i];
        if (
          a.start !== null &&
          a.start !== '' &&
          a.end !== null &&
          a.end !== '' &&
          a.start >= a.end
        ) {
          timeSlots[i].control.setErrors({ endBeforeStart: true });
          timeSlots[i].control
            .get('endTime')!
            .setErrors({ endBeforeStart: true });
        }
      }
    }
    return null;
  };
}

export function hasUnAvailableTimeSlotOverlaps(
  timeSlots: any[],
  newTimeSlot: any
): boolean {
  for (const timeSlot of timeSlots) {
    if (
      newTimeSlot.startTime != '' &&
      timeSlot.startTime != '' &&
      newTimeSlot.startTime !== null &&
      timeSlot.startTime !== null &&
      newTimeSlot.startTime >= timeSlot.startTime &&
      newTimeSlot.startTime <= timeSlot.endTime
    ) {
      return true;
    }
  }
  return false;
}

export function unavailabilityOverlapsWithAvailabilityValidator(
  availability: AbstractControl
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (availability instanceof FormArray && control instanceof FormArray) {
      const availableTimeSlots = availability.controls.map(
        (control) => control.value
      );

      for (const unavailabilityControl of control.controls) {
        const unavailabilityTimeSlot = unavailabilityControl.value;
        if (
          hasUnAvailableTimeSlotOverlaps(
            availableTimeSlots,
            unavailabilityTimeSlot
          )
        ) {
          unavailabilityControl.get('startTime')!.setErrors({ unavailabilityOverlaps: true });
          unavailabilityControl.get('endTime')!.setErrors({ unavailabilityOverlaps: true });
          unavailabilityControl.setErrors({ unavailabilityOverlaps: true });
          return { unavailabilityOverlaps: true };
        }
      }
    }
    return null;
  };
}

export function hasAvailableTimeSlotOverlaps(
  timeSlots: any[],
  newTimeSlot: any
): boolean {
  for (const timeSlot of timeSlots) {
    if (
      newTimeSlot.startTime != '' &&
      timeSlot.startTime != '' &&
      newTimeSlot.startTime !== null &&
      timeSlot.startTime !== null &&
      newTimeSlot.startTime <= timeSlot.endTime &&
      newTimeSlot.endTime >= timeSlot.startTime
    ) {
      return true;
    }
  }
  return false;
}

export function availabilityOverlapsWithUnavailabilityValidator(
  unavailability: AbstractControl
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (unavailability instanceof FormArray && control instanceof FormArray) {
      const unavailabilityTimeSlots = unavailability.controls.map(
        (control) => control.value
      );

      for (const availabilityControl of control.controls) {
        const availabilityTimeSlot = availabilityControl.value;
        if (
          hasAvailableTimeSlotOverlaps(
            unavailabilityTimeSlots,
            availabilityTimeSlot
          )
        ) {
          availabilityControl.get('startTime')!.setErrors({ availabilityOverlaps: true });
          availabilityControl.get('endTime')!.setErrors({ availabilityOverlaps: true });
          availabilityControl.setErrors({ availabilityOverlaps: true });
          return { availabilityOverlaps: true };
        }
      }
    }
    return null;
  };
}

export function startEndTimeRequired(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control && control instanceof FormArray) {
      for (const ctrl of control.controls) {
        const timeSlot = ctrl.value;
        const startTime = timeSlot.startTime;
        const endTime = timeSlot.endTime;

        if (
          startTime !== '' &&
          startTime !== null &&
          (endTime == '' || endTime == null)
        ) {
          ctrl.get('endTime')!.addValidators(Validators.required);
          ctrl.get('endTime')!.setErrors({ startEndRequired: true });
          ctrl.addValidators(Validators.required);
          ctrl.setErrors({ startEndRequired: true });
          return { startEndRequired: true };
        } else if (
          (startTime == '' || startTime == null) &&
          endTime !== '' &&
          endTime !== null
        ) {
          ctrl.get('startTime')!.addValidators(Validators.required);
          ctrl.get('startTime')!.setErrors({ startEndRequired: true });
          ctrl.addValidators(Validators.required);
          ctrl.setErrors({ startEndRequired: true });
          return { startEndRequired: true };
        } else {
          if (
            startTime !== '' &&
            startTime !== null &&
            (endTime !== '' || endTime !== null) &&
            startTime >= endTime
          ) {
            ctrl.get('endTime')!.setErrors({ endBeforeStart: true });
            ctrl.setErrors({ endBeforeStart: true });
            return { startEndRequired: true };
          } else {
            ctrl.get('startTime')!.removeValidators(Validators.required);
            ctrl.get('endTime')!.removeValidators(Validators.required);
            ctrl.get('startTime')!.setErrors(null);
            ctrl.get('endTime')!.setErrors(null);
            ctrl.removeValidators(Validators.required);
          }
        }
      }
    }
    return null;
  };
}
